import React, { useState } from 'react';
import '../InvoiceDetail.css';

/**
 * MultiEmailField - Component for managing multiple email addresses
 *
 * Props:
 *   - emails: Array of email strings (can also be string for backward compatibility)
 *   - onChange: Callback when emails change
 *   - label: Label for the field
 *   - readOnly: If true, displays emails without edit capability
 */
export default function MultiEmailField({ emails, onChange, label = "Email", readOnly = false }) {
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Normalize emails to always be an array
  const normalizeEmails = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      // Handle comma-separated or single email
      return value.includes(',')
        ? value.split(',').map(e => e.trim()).filter(e => e)
        : [value];
    }
    return [];
  };

  const emailList = normalizeEmails(emails);

  // Validate email format
  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Add new email
  const handleAddEmail = () => {
    const trimmedEmail = newEmail.trim().toLowerCase();

    if (!trimmedEmail) {
      setError('Introduceți o adresă de email');
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError('Format email invalid');
      return;
    }

    if (emailList.includes(trimmedEmail)) {
      setError('Acest email există deja');
      return;
    }

    const updatedEmails = [...emailList, trimmedEmail];
    onChange(updatedEmails);
    setNewEmail('');
    setError('');
    setIsAdding(false);
  };

  // Cancel adding
  const handleCancelAdd = () => {
    setNewEmail('');
    setError('');
    setIsAdding(false);
  };

  // Remove email
  const handleRemoveEmail = (index) => {
    const updatedEmails = emailList.filter((_, i) => i !== index);
    onChange(updatedEmails.length > 0 ? updatedEmails : null);
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  if (readOnly) {
    // Read-only display
    return (
      <div className="form-field">
        <div className="form-field-label">{label}</div>
        <div className="form-field-value">
          {emailList.length > 0 ? (
            <div className="email-list-readonly">
              {emailList.map((email, index) => (
                <span key={index} className="email-tag-readonly">
                  {email}
                </span>
              ))}
            </div>
          ) : (
            '—'
          )}
        </div>
      </div>
    );
  }

  // Editable mode
  return (
    <div className="form-field">
      <div className="form-field-label">{label}</div>
      <div className="email-field-container">
        {/* Existing emails display */}
        <div className="email-display-box">
          {emailList.length > 0 ? (
            <div className="email-list">
              {emailList.map((email, index) => (
                <span key={index} className="email-tag">
                  <span className="email-tag-text">{email}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveEmail(index)}
                    className="email-tag-remove"
                    title="Șterge email"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <span className="email-placeholder">—</span>
          )}

          {/* Add button */}
          {!isAdding && (
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              className="email-add-trigger"
              title="Adaugă email"
            >
              +
            </button>
          )}
        </div>

        {/* Add email input (shown when isAdding is true) */}
        {isAdding && (
          <div className="email-input-section">
            <div className="email-input-group">
              <input
                type="email"
                className="clients-input"
                placeholder="adresa@email.com"
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value);
                  setError('');
                }}
                onKeyPress={handleKeyPress}
                autoFocus
              />
              <button
                type="button"
                onClick={handleAddEmail}
                className="email-action-btn email-action-btn-add"
              >
                Adaugă
              </button>
              <button
                type="button"
                onClick={handleCancelAdd}
                className="email-action-btn email-action-btn-cancel"
              >
                Anulează
              </button>
            </div>
            {error && (
              <p className="email-error">{error}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
